import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { getReplay, backendSource, getMarketData, resolveOnTick, computeStats, rMultiple, tradePnl } from "../engine";
import { api } from "../lib/api";

const ReplayContext = createContext(null);

// Exposes ReplayEngine state to React + the Replay Paper Trading session.
// `sessionKey` changes whenever the data source for a symbol flips (live ↔ replay, or a new replay session).
export function ReplayProvider({ children }) {
  const [state, setState] = useState(getReplay().state);
  const [trades, setTrades] = useState([]);
  const tradesRef = useRef([]);
  const sessionRef = useRef(null);

  useEffect(() => {
    const replay = getReplay();
    replay.setFallbackSource(backendSource(async (symbol, from, to, limit) => {
      const { data } = await api.get("/ticks", { params: { symbol, from, to, limit } });
      return data.ticks;
    }));
    return replay.onState(setState);
  }, []);

  const setAll = useCallback((next) => { tradesRef.current = next; setTrades(next); }, []);

  // New replay session → new trading session (trades are kept per session id in felacoo_positions)
  useEffect(() => {
    if (!state.active) { sessionRef.current = null; setAll([]); return; }
    const sid = `rp-${state.sessionId}-${state.symbol}-${state.startTime}`;
    if (sessionRef.current !== sid) {
      sessionRef.current = sid;
      setAll([]);
      api.get("/positions", { params: { mode: "replay", session_id: sid, status: "all" } })
        .then(({ data }) => setAll(data.positions.map(fromApi))).catch(() => {});
    }
  }, [state.active, state.sessionId, state.symbol, state.startTime, setAll]);

  const closeTrade = useCallback(async (id, exit, reason, exitTime) => {
    const t = tradesRef.current.find((x) => x.id === id);
    if (!t || t.status === "closed") return;
    const r = rMultiple(t, exit);
    const next = tradesRef.current.map((x) => (x.id === id ? { ...x, status: "closed", exit, reason, exitTime, r, pnl: tradePnl(x, exit) } : x));
    setAll(next);
    toast(`${reason === "tp" ? "Target hit" : reason === "sl" ? "Stopped out" : "Closed"} · ${t.side.toUpperCase()} ${t.symbol}`, {
      description: `Exit ${exit.toFixed(2)} · ${r != null ? `${r >= 0 ? "+" : ""}${r.toFixed(2)}R` : `${tradePnl(t, exit).toFixed(2)} P/L`}`,
    });
    try { await api.post(`/positions/${id}/close`, { exit_price: exit, reason, exit_time: exitTime }); } catch {}
  }, [setAll]);

  // Tick-level SL/TP resolution: subscribe to the (replayed) feed for the session symbol.
  useEffect(() => {
    if (!state.active) return;
    const md = getMarketData();
    const off = md.subscribe(state.symbol, (ticks, initial) => {
      if (initial) return; // resets (step back / seek) never re-resolve trades
      for (const tick of ticks) {
        for (const t of tradesRef.current) {
          const hit = resolveOnTick(t, tick);
          if (hit) closeTrade(t.id, hit.exit, hit.reason, tick.time);
        }
      }
    });
    return off;
  }, [state.active, state.sessionId, state.symbol, closeTrade]);

  const openTrade = useCallback(async ({ symbol, display, side, lot, entry, stopLoss, takeProfit, snapshot }) => {
    const sid = sessionRef.current;
    if (!sid) throw new Error("no replay session");
    const { data } = await api.post("/positions", {
      symbol, display, side, lot, entry, mode: "replay", session_id: sid, stop_loss: stopLoss, take_profit: takeProfit,
      sl_points: stopLoss != null ? Math.abs(entry - stopLoss) : null, tp_points: takeProfit != null ? Math.abs(takeProfit - entry) : null,
      market_time: getReplay().state.cursor, setup_snapshot: snapshot ?? null,
    });
    const t = fromApi(data.position);
    setAll([t, ...tradesRef.current]);
    return t;
  }, [setAll]);

  const stats = useMemo(() => computeStats(trades), [trades]);

  const value = useMemo(() => ({
    replay: state,
    engine: getReplay(),
    sessionKey: state.active ? `replay-${state.sessionId}-${state.symbol}` : "live",
    isReplaying: (symbol) => state.active && state.symbol === symbol,
    trades, stats, openTrade,
    closeTradeManual: (id, price) => closeTrade(id, price, "manual", getReplay().state.cursor),
  }), [state, trades, stats, openTrade, closeTrade]);

  return <ReplayContext.Provider value={value}>{children}</ReplayContext.Provider>;
}

function fromApi(p) {
  return {
    id: p.id, symbol: p.symbol, display: p.display, side: p.side, lot: p.lot, entry: p.entry,
    stopLoss: p.stop_loss ?? null, takeProfit: p.take_profit ?? null, openedAt: p.market_time ?? 0,
    status: p.status, exit: p.exit ?? undefined, exitTime: p.exit_time ?? undefined, reason: p.close_reason ?? undefined,
    r: p.r_multiple ?? (p.exit != null ? rMultiple({ entry: p.entry, stopLoss: p.stop_loss, side: p.side }, p.exit) : null),
    pnl: p.pnl, snapshot: p.setup_snapshot,
  };
}

export const useReplay = () => useContext(ReplayContext);
